import React, { useEffect, useState } from "react";
import { 
    Modal, Button, Typography, Box, Container, Chip, Pagination, IconButton, Tooltip, CircularProgress
} from '@mui/material';
import { 
    Add as AddIcon, 
    ReceiptLong as ReceiptIcon, 
    Done as DoneIcon, 
    ArrowForward as ArrowForwardIcon 
} from '@mui/icons-material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { image } from "../../common/Theme";
import { useLocation, useNavigate } from 'react-router-dom';
import Apiconnection from "../../utils/Apiconnection";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Header } from "../../component/Header";

const calculateDuration = (fromDate, inTime, toDate, outTime) => {
    if (!fromDate || !inTime || !toDate || !outTime) return "Duration: N/A";
    const start = dayjs(`${dayjs(fromDate).format('YYYY-MM-DD')} ${inTime}`);
    const end = dayjs(`${dayjs(toDate).format('YYYY-MM-DD')} ${outTime}`);
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes <= 0) return "Duration: 0 Hrs";
    
    const days = Math.floor(diffMinutes / (24 * 60));
    const hours = Math.floor((diffMinutes % (24 * 60)) / 60);
    const minutes = diffMinutes % 60;
    
    let parts = [];
    if (days > 0) parts.push(`${days} Day${days > 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} Hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} Min${minutes > 1 ? 's' : ''}`);
    
    return `Duration: ${parts.join(' ')}`;
};

const getUpcomingCount = (bookings) => {
    const now = dayjs();
    const next7Days = now.add(7, 'day');
    return bookings.filter(b => {
        if (b.bookingStatus !== 'Pending') return false;
        const checkInDateTime = dayjs(`${dayjs(b.fromDate).format('YYYY-MM-DD')} ${b.selectedCheckInTime}`);
        return checkInDateTime.isAfter(now) && checkInDateTime.isBefore(next7Days);
    }).length;
};

const BookingList = () => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openModal, setOpenModal] = useState(false);
    const [confirmReadyModal, setConfirmReadyModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const { email, password, customerId, firstName, lastName, phone, payload } = location.state || {};
    const navigate = useNavigate();

    const handleRequestMarkReady = async (bookingId) => {
        const payload = { bookingRequestId: bookingId };
        try {
            const response = await Apiconnection.post('request-to-mark-ready', payload);
            if (response.data.status) {
                const updatedBookings = bookings.map((booking) => {
                    if (booking.id === bookingId) {
                        return { ...booking, bookingStatus: "Ready", readyStatus: "Requested" };
                    }
                    return booking;
                });
                setBookings(updatedBookings);
                setFilteredBookings(updatedBookings);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleRequestReadyClick = (bookingId) => {
        setSelectedBookingId(bookingId);
        setConfirmReadyModal(true);
    };

    const handleConfirmRequestReady = async () => {
        if (selectedBookingId !== null) {
            await handleRequestMarkReady(selectedBookingId);
        }
        setConfirmReadyModal(false);
        setSelectedBookingId(null);
    };

    const handleCancelRequestReady = () => {
        setConfirmReadyModal(false);
        setSelectedBookingId(null);
    };

    const handleOpenModal = () => {
        const safePhone = phone != null ? String(phone).trim() : "";
        navigate('/booking-create', { state: { email, password, customerId, firstName, lastName, phone: safePhone, payload } });
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleCheckIn = (hasReservation) => {
        if (hasReservation) {
            navigate('/customer-booking');
        } else {
            navigate('/add-booking');
        }
        setOpenModal(false);
    };

    const handleViewInvoice = async (bookingRequestId) => {
        navigate('/booking-receipt', { state: { bookingRequestId } });
    };

    const fetchBookings = async () => {
        setLoading(true);
        try {
            const response = await Apiconnection.post(`customer-booking-history?page=1&limit=1000`);
            if (response.data.status) {
                console.log("response",response.data)
                setBookings(response.data.data);
                setFilteredBookings(response.data.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('prime_access_token');
        if (token) {
            Apiconnection.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            navigate('/login');
        }
        fetchBookings();
    }, []);

    const handleProfileClick = () => navigate('/my-profile');
    const handleChangePasswordClick = () => navigate('/change-password');

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = Math.ceil(filteredBookings.length / rowsPerPage);
    const paginatedBookings = filteredBookings.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage);

    const getStatusColor = (status) => {
        switch(status) {
            case 'Checked In': return { bg: '#e6f4ea', text: '#1e8e3e' };
            case 'Completed': return { bg: '#e8f0fe', text: '#1a73e8' };
            case 'Cancelled': return { bg: '#fce8e6', text: '#d93025' };
            default: return { bg: '#f1f5f9', text: '#475569' };
        }
    };

    return (
        <div
            className="booking-main booking-create-page"
            style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', position: 'relative' }}
        >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '350px', backgroundColor: '#001b44', backgroundImage: `url(${image.bodybg})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }}></div>
            <div className="booking-inner booking-create-inner" style={{ position: 'relative', zIndex: 1 }}>
                <Header />
                
                <div>
                    <Container maxWidth="xl" sx={{ mt: 2, pb: 8, fontFamily: "'Inter', 'Poppins', sans-serif", backgroundColor: '#fff', borderRadius: '24px', p: { xs: 3, md: 5 }, boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}>
                {/* Header Section */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 4
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: '60px', height: '60px', backgroundColor: '#f0f7ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CalendarTodayOutlinedIcon style={{ color: '#004777', fontSize: '28px' }} />
                        </Box>
                        <Box>
                            <Typography variant="h3" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 800, color: '#1a1a1a', letterSpacing: '-1px', mb: 0.5, fontSize: { xs: '28px', sm: '32px' } }}>
                                Reservation Overview
                            </Typography>
                            <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', fontWeight: 500 }}>
                                Manage your upcoming and past parking sessions seamlessly.
                            </Typography>
                        </Box>
                    </Box>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        onClick={handleOpenModal}
                        sx={{ 
                            fontFamily: "'Inter', 'Poppins', sans-serif",
                            backgroundColor: '#004777', 
                            color: 'white', 
                            borderRadius: '100px', 
                            px: 4, 
                            py: 1.5, 
                            fontWeight: 700, 
                            textTransform: 'none',
                            boxShadow: '0 8px 20px rgba(0, 71, 119, 0.25)',
                            '&:hover': { backgroundColor: '#003355' },
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        New Reservation
                    </Button>
                </Box>

                {/* Summary Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '16px', p: 2.5, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: '#fff', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 10px 25px rgba(0,0,0,0.05)', transform: 'translateY(-3px)' } }}>
                        <Box sx={{ width: '56px', height: '56px', backgroundColor: '#f0f7ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CalendarTodayOutlinedIcon style={{ color: '#0b57d0' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#64748b', fontSize: '13px', fontWeight: 600, mb: 0.5 }}>Total Reservations</Typography>
                            <Typography sx={{ color: '#0b57d0', fontSize: '24px', fontWeight: 800, lineHeight: 1 }}>{bookings.length}</Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: '12px', mt: 0.5 }}>All time bookings</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '16px', p: 2.5, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: '#fff', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 10px 25px rgba(0,0,0,0.05)', transform: 'translateY(-3px)' } }}>
                        <Box sx={{ width: '56px', height: '56px', backgroundColor: '#ebfdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DoneIcon style={{ color: '#22c55e' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#64748b', fontSize: '13px', fontWeight: 600, mb: 0.5 }}>Upcoming</Typography>
                            <Typography sx={{ color: '#22c55e', fontSize: '24px', fontWeight: 800, lineHeight: 1 }}>{getUpcomingCount(bookings)}</Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: '12px', mt: 0.5 }}>Next 7 days</Typography>
                        </Box>
                    </Box>
                    {/* <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '16px', p: 2.5, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: '#fff', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 10px 25px rgba(0,0,0,0.05)', transform: 'translateY(-3px)' } }}>
                        <Box sx={{ width: '56px', height: '56px', backgroundColor: '#f3e8ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccessTimeOutlinedIcon style={{ color: '#9333ea' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#64748b', fontSize: '13px', fontWeight: 600, mb: 0.5 }}>Completed</Typography>
                            <Typography sx={{ color: '#9333ea', fontSize: '24px', fontWeight: 800, lineHeight: 1 }}>{bookings.filter(b => b.bookingStatus === 'Completed').length || 0}</Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: '12px', mt: 0.5 }}>Past bookings</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '16px', p: 2.5, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: '#fff', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 10px 25px rgba(0,0,0,0.05)', transform: 'translateY(-3px)' } }}>
                        <Box sx={{ width: '56px', height: '56px', backgroundColor: '#fff7ed', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccessTimeOutlinedIcon style={{ color: '#f97316' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#64748b', fontSize: '13px', fontWeight: 600, mb: 0.5 }}>Cancelled</Typography>
                            <Typography sx={{ color: '#f97316', fontSize: '24px', fontWeight: 800, lineHeight: 1 }}>{bookings.filter(b => b.bookingStatus === 'Cancelled').length || 0}</Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: '12px', mt: 0.5 }}>All time</Typography>
                        </Box>
                    </Box> */}
                </Box>

                {/* Quick Tip */}
                <Box sx={{ backgroundColor: '#f0f7ff', borderRadius: '12px', p: 2, mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                        <Box sx={{ width: '24px', height: '24px', backgroundColor: '#0b57d0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>i</Box>
                        <Typography sx={{ color: '#0b57d0', fontWeight: 700, fontSize: '14px' }}>Quick Tip:</Typography>
                        <Typography sx={{ color: '#475569', fontSize: '14px' }}>You can view, download receipts and manage your reservations from here.</Typography>
                    </Box>
                    <Box sx={{ opacity: 0.3, right: 20, position: 'absolute', top: '-10px' }}>
                        <img src={image.car} alt="car" style={{ height: '70px', objectFit: 'contain' }} />
                    </Box>
                </Box>

                {/* Horizontal List Container */}
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
                        <CircularProgress sx={{ color: '#000000', mb: 2 }} size={50} thickness={4} />
                        <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#000000', fontWeight: 600, fontSize: '1.1rem' }}>
                            Loading Reservations...
                        </Typography>
                    </Box>
                ) : paginatedBookings.length > 0 ? (
                    <Box sx={{ 
                        backgroundColor: '#fff',
                        
                        borderRadius: '24px',
                        
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden'
                    }}>
                        {/* Table Header Row (Hidden on mobile) */}
                        <Box sx={{ 
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            px: 4, py: 2.5,
                            borderBottom: '2px solid #f1f5f9',
                            backgroundColor: '#f8fafc'
                        }}>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '10%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Ticket ID</Typography>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '20%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Customer Details</Typography>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '10%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Slot</Typography>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '15%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Start Date & Time</Typography>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '15%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>End Date & Time</Typography>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '15%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Action</Typography>
                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", width: '15%', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>Invoice</Typography>
                        </Box>

                        {/* List Items */}
                        {paginatedBookings.map((booking, index) => {
                            const statusColors = getStatusColor(booking.bookingStatus);
                            const isLast = index === paginatedBookings.length - 1;
                            
                            return (
                                <Box key={booking.id} sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    px: { xs: 3, md: 4 }, 
                                    py: { xs: 3, md: 3 },
                                    borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                                    transition: 'all 0.2s ease',
                                    gap: { xs: 2, md: 0 },
                                    '&:hover': {
                                        backgroundColor: '#f8fafc'
                                    }
                                }}>
                                    
                                    {/* Mobile Only Header for Ticket + Status */}
                                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between', width: '100%', mb: 1 }}>
                                        <Box sx={{ backgroundColor: '#f0f4f8', px: 1.5, py: 0.5, borderRadius: '6px', fontWeight: 700, color: '#004777', fontSize: '0.85rem', fontFamily: "'Inter', 'Poppins', sans-serif" }}>
                                            #{booking.bookingTicketNo}
                                        </Box>
                                        <Chip 
                                            label={booking.bookingStatus || 'Pending'}
                                            size="small"
                                            sx={{ backgroundColor: statusColors.bg, color: statusColors.text, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: "'Inter', 'Poppins', sans-serif" }} 
                                        />
                                    </Box>

                                    {/* Column 1: Ticket ID (Desktop) */}
                                    <Box sx={{ width: { xs: '100%', md: '10%' }, display: { xs: 'none', md: 'block' } }}>
                                        <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                            {booking.bookingTicketNo}
                                        </Typography>
                                    </Box>

                                    {/* Column 2: Customer Details */}
                                    <Box sx={{ width: { xs: '100%', md: '20%' }, display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ 
                                            width: 32, height: 32, borderRadius: '50%', 
                                            backgroundColor: '#e2e8f0',
                                            display: 'flex', justifyContent: 'center', alignItems: 'center', 
                                            mr: 1.5, color: '#64748b', fontWeight: 700, fontSize: '0.75rem',
                                            fontFamily: "'Inter', 'Poppins', sans-serif"
                                        }}>
                                            {(booking.user?.firstName?.charAt(0) || '') + (booking.user?.lastName?.charAt(0) || '')}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                                {booking.user?.firstName} {booking.user?.lastName}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                                                {booking.customerMobile}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Column 3: Slot */}
                                    <Box sx={{ width: { xs: '100%', md: '10%' } }}>
                                        <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                            {booking.slot?.slotName || 'N/A'}
                                        </Typography>
                                    </Box>

                                    {/* Column 4: Start Date & Time */}
                                    <Box sx={{ width: { xs: '100%', md: '15%' } }}>
                                        <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                            {dayjs(booking.fromDate).format('YYYY-MM-DD')} | {booking.selectedCheckInTime}
                                        </Typography>
                                    </Box>

                                    {/* Column 5: End Date & Time */}
                                    <Box sx={{ width: { xs: '100%', md: '15%' } }}>
                                        <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                            {dayjs(booking.toDate).format('YYYY-MM-DD')} | {booking.selectedCheckOutTime}
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip 
                                                icon={<AccessTimeOutlinedIcon style={{ fontSize: '12px' }} />}
                                                label={calculateDuration(booking.fromDate, booking.selectedCheckInTime, booking.toDate, booking.selectedCheckOutTime).replace('Duration: ', '')}
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: '#f1f5f9', 
                                                    color: '#64748b', 
                                                    fontWeight: 600, 
                                                    fontSize: '0.65rem', 
                                                    height: '20px',
                                                    '& .MuiChip-label': { px: 1 },
                                                    fontFamily: "'Inter', 'Poppins', sans-serif" 
                                                }} 
                                            />
                                        </Box>
                                    </Box>
                                    {/* Column 6: Action */}
                                    <Box sx={{ width: { xs: '100%', md: '15%' }, display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' }, alignItems: 'center' }}>
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={() => handleRequestReadyClick(booking.id)} 
                                            sx={{ 
                                                fontFamily: "'Inter', 'Poppins', sans-serif",
                                                borderRadius: '6px',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                px: 2,
                                                fontSize: '0.75rem',
                                                whiteSpace: 'nowrap',
                                                '&.Mui-disabled': {
                                                    backgroundColor: '#e2e8f0 !important',
                                                    color: '#94a3b8 !important',
                                                }
                                            }}
                                            disabled={booking.bookingStatus !== "Checked In" || booking.readyStatus === "Requested" || booking.readyStatus === "Ready"}
                                        >
                                            {booking.readyStatus === "Not Requested" ? "Request Ready" : booking.readyStatus === "Requested" ? "In-Progress" : "Request Accepted"} 
                                        </Button>
                                    </Box>

                                    {/* Column 7: Invoice */}
                                    <Box sx={{ width: { xs: '100%', md: '15%' }, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
                                        <Button 
                                            variant="contained" 
                                            onClick={() => handleViewInvoice(booking.id)}
                                            sx={{ 
                                                fontFamily: "'Inter', 'Poppins', sans-serif",
                                                backgroundColor: '#004777', 
                                                color: 'white',
                                                borderRadius: '6px',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                px: 2,
                                                fontSize: '0.75rem',
                                                whiteSpace: 'nowrap',
                                                '&:hover': { backgroundColor: '#003355' }
                                            }}
                                        >
                                            View Invoice
                                        </Button>
                                    </Box>

                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                        borderRadius: '24px', 
                        py: 10, 
                        textAlign: 'center',
                        border: '1px solid rgba(255,255,255,0.5)'
                    }}>
                        <Box sx={{ 
                            width: '100px', 
                            height: '100px', 
                            backgroundColor: '#f1f5f9', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            margin: '0 auto 24px auto' 
                        }}>
                            <Typography sx={{ fontSize: '40px' }}>📅</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 800, color: '#1e293b', mb: 1.5 }}>
                            No Reservations Found
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', fontWeight: 500 }}>
                            You haven't made any parking reservations yet.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={handleOpenModal}
                            sx={{ 
                                mt: 4,
                                fontFamily: "'Inter', 'Poppins', sans-serif",
                                backgroundColor: '#004777', 
                                color: 'white', 
                                borderRadius: '100px', 
                                px: 4, 
                                py: 1.5, 
                                fontWeight: 700, 
                                textTransform: 'none',
                                boxShadow: '0 8px 20px rgba(0, 71, 119, 0.25)'
                            }}
                        >
                            Book Parking Now
                        </Button>
                    </Box>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={5}>
                        <Pagination 
                            count={totalPages} 
                            page={page} 
                            onChange={handlePageChange} 
                            color="primary" 
                            size="large"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    fontFamily: "'Inter', 'Poppins', sans-serif",
                                    fontWeight: 700,
                                    borderRadius: '12px'
                                }
                            }}
                        />
                    </Box>
                )}

                {/* Trust Banners Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5, pt: 3, borderTop: '1px solid #e2e8f0', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: '40px', height: '40px', backgroundColor: '#f0f7ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LockOutlinedIcon style={{ color: '#0b57d0' }} /></Box>
                        <Box><Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Secure & Reliable</Typography><Typography sx={{ color: '#64748b', fontSize: '12px' }}>Your data is always protected</Typography></Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: '40px', height: '40px', backgroundColor: '#ebfdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldOutlinedIcon style={{ color: '#22c55e' }} /></Box>
                        <Box><Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Hassle-Free Parking</Typography><Typography sx={{ color: '#64748b', fontSize: '12px' }}>Book, park & go with ease</Typography></Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: '40px', height: '40px', backgroundColor: '#f3e8ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><HeadsetMicOutlinedIcon style={{ color: '#9333ea' }} /></Box>
                        <Box><Typography sx={{ fontWeight: 700, fontSize: '13px' }}>24/7 Support</Typography><Typography sx={{ color: '#64748b', fontSize: '12px' }}>We're here to help anytime</Typography></Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: '40px', height: '40px', backgroundColor: '#fff7ed', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><StarBorderOutlinedIcon style={{ color: '#f97316' }} /></Box>
                        <Box><Typography sx={{ fontWeight: 700, fontSize: '13px' }}>Trusted by Thousands</Typography><Typography sx={{ color: '#64748b', fontSize: '12px' }}>Thank you for choosing Prime Park</Typography></Box>
                    </Box>
                </Box>

                    </Container>
                </div>
            </div>

            {/* Reservation Modal */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
            >
                <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    backgroundColor: 'white', 
                    padding: '40px 30px', 
                    borderRadius: '24px',
                    width: '90%',
                    maxWidth: '450px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    textAlign: 'center',
                    fontFamily: "'Inter', 'Poppins', sans-serif"
                }}>
                    <Typography variant="h5" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 800, color: '#1a1a1a', mb: 2 }}>Reservation Check</Typography>
                    <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', mb: 4, fontWeight: 500 }}>Does the customer have an existing reservation with us?</Typography>
                    <Box display="flex" gap={2} justifyContent="center">
                        <Button 
                            variant="contained" 
                            onClick={() => handleCheckIn(true)}
                            sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", backgroundColor: '#004777', borderRadius: '100px', py: 1.5, fontWeight: 700, flex: 1, textTransform: 'none', fontSize: '16px' }}
                        >
                            Yes
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={() => handleCheckIn(false)}
                            sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", borderColor: '#004777', color: '#004777', borderRadius: '100px', py: 1.5, fontWeight: 700, flex: 1, borderWidth: '2px', textTransform: 'none', fontSize: '16px', '&:hover': { borderWidth: '2px' } }}
                        >
                            No
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Request Ready Confirmation Modal */}
            <Modal
                open={confirmReadyModal}
                onClose={handleCancelRequestReady}
            >
                <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    backgroundColor: 'white', 
                    padding: '40px 30px', 
                    borderRadius: '24px',
                    width: '90%',
                    maxWidth: '450px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    textAlign: 'center',
                    fontFamily: "'Inter', 'Poppins', sans-serif"
                }}>
                    <Typography variant="h5" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 800, color: '#1a1a1a', mb: 2 }}>Confirm Ready Request</Typography>
                    <Typography variant="body1" sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', mb: 4, fontWeight: 500 }}>Are you sure you want to request ready for this booking?</Typography>
                    <Box display="flex" gap={2} justifyContent="center">
                        <Button 
                            variant="contained" 
                            onClick={handleConfirmRequestReady}
                            sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", backgroundColor: '#004777', borderRadius: '100px', py: 1.5, fontWeight: 700, flex: 1, textTransform: 'none', fontSize: '16px' }}
                        >
                            Yes
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={handleCancelRequestReady}
                            sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", borderColor: '#004777', color: '#004777', borderRadius: '100px', py: 1.5, fontWeight: 700, flex: 1, borderWidth: '2px', textTransform: 'none', fontSize: '16px', '&:hover': { borderWidth: '2px' } }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default BookingList;
